import React, { useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Home as HomeIcon,
  Dumbbell,
  Sparkles,
  ClipboardCheck,
  Timer,
  LineChart,
  Trophy,
  User as UserIcon,
  LogOut,
  Bot,
} from "lucide-react";
import { B2CGuard } from "@/lib/avanzoGuard";
import { NeuralBackground } from "@/components/NeuralBackground";
import BuddyAvanzaLegalFooter from "@/components/BuddyAvanzaLegalFooter";
import AvanzoOnboardingTour from "./AvanzoOnboardingTour";
import { S1Announcement } from "./S1Announcement";
import AvanzoTeacherPanel from "./AvanzoTeacherPanel";
import { avanzoApi } from "@/lib/avanzoApi";
import { API } from "@/App";
import "./avanzo.css";

// Rutas REALES existentes en /app/frontend/src/App.js
// F3 · BUDDY TRAINER added; Progress Check hidden from student-facing nav
// (route/backend/pool preserved — see /app/backend/routes/reto_avanzo.py).
const LINKS = [
  { to: "/avanzo",                  label: "Inicio",         icon: HomeIcon,        testid: "avz-nav-home" },
  { to: "/avanzo/entrenar",         label: "Entrenamiento",  icon: Dumbbell,        testid: "avz-nav-train" },
  { to: "/avanzo/buddy",            label: "BUDDY",          icon: Bot,             testid: "avz-nav-buddy" },
  { to: "/avanzo/practica",         label: "Práctica",       icon: Sparkles,        testid: "avz-nav-practice" },
  { to: "/avanzo/simulacro",        label: "Simulacro",      icon: Timer,           testid: "avz-nav-sim" },
  { to: "/avanzo/progreso",         label: "Mi progreso",    icon: LineChart,       testid: "avz-nav-progress" },
];

// Locked item — visually present per reference design, no route (feature pending).
const LOCKED_LINKS = [
  { label: "Logros", icon: Trophy, testid: "avz-nav-achievements-locked" },
];

const FOOTER_LINKS = [
  { to: "/avanzo/perfil",   label: "Mi perfil", icon: UserIcon, testid: "avz-nav-profile" },
];

function LogoutButton({ variant = "desktop" }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const handleLogout = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch { /* server session may already be invalid */ }
    try { localStorage.removeItem("token"); } catch { /* ignore */ }
    navigate("/login", { replace: true });
  };

  if (variant === "mobile") {
    return (
      <button
        type="button"
        onClick={handleLogout}
        disabled={busy}
        className="avz-tabbar-item avz-tabbar-logout"
        data-testid="avz-nav-logout-mob"
        aria-label="Cerrar sesión"
      >
        <LogOut className="avz-tabbar-icon" aria-hidden="true" />
        <span>Salir</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleLogout}
      disabled={busy}
      className="avz-side-item avz-side-logout"
      data-testid="avz-nav-logout"
      aria-label="Cerrar sesión"
    >
      <LogOut className="avz-side-icon" aria-hidden="true" />
      <span>{busy ? "Saliendo…" : "Cerrar sesión"}</span>
    </button>
  );
}

function AvanzoStaffLanding({ role, brand }) {
  const navigate = useNavigate();
  const label = role === "teacher" ? "Docente"
              : role === "institutional_admin" ? "Administración institucional"
              : "Personal institucional";
  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch {}
    try { localStorage.removeItem("token"); } catch {}
    navigate("/login", { replace: true });
  };
  return (
    <div className="avz-shell avz-shell--legacy" data-testid="avz-staff-landing">
      <header className="avz-nav">
        <div className="avz-nav-inner">
          <div className="avz-brand" data-testid="avz-brand">
            <img src="/qhub-buddy-logo.png" alt="QHUB.BUDDY" className="avz-brand-logo" />
            <span className="avz-brand-sub">{brand || "Preparación AVANZO"}</span>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="avz-nav-logout"
            data-testid="avz-staff-logout"
            aria-label="Cerrar sesión"
          >
            <LogOut className="avz-nav-logout-icon" />
            <span>Cerrar sesión</span>
          </button>
        </div>
      </header>
      <main className="avz-container">
        <section
          style={{ padding: "3rem 1rem", maxWidth: 720, margin: "0 auto", textAlign: "center" }}
        >
          <h1 style={{ fontSize: "1.75rem", marginBottom: "0.75rem" }}>
            Buddy Avanza — acceso institucional activo
          </h1>
          <p style={{ opacity: 0.85, marginBottom: "1rem" }}>
            Estás autenticado como <strong>{label}</strong>. Tu institución
            tiene Buddy Avanza habilitado.
          </p>
          <p style={{ opacity: 0.7, fontSize: "0.95rem" }}>
            La vista de seguimiento pedagógico para docentes y administradores
            estará disponible próximamente. Los estudiantes de tu institución
            ya pueden practicar y presentar simulacros.
          </p>
        </section>
      </main>
      <BuddyAvanzaLegalFooter dense />
    </div>
  );
}

function Sidebar() {
  return (
    <aside className="avz-sidebar" data-testid="avz-sidebar" aria-label="Navegación principal">
      <div className="avz-side-brand" data-testid="avz-brand">
        <img
          src="/buddy-avanza-logo.png"
          alt="Buddy Avanza"
          className="avz-side-logo"
          data-testid="avz-brand-logo"
        />
      </div>

      <nav className="avz-side-nav" aria-label="Sección principal">
        {LINKS.map((l) => {
          const Icon = l.icon;
          return (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/avanzo"}
              className="avz-side-item"
              data-testid={l.testid}
            >
              <Icon className="avz-side-icon" aria-hidden="true" />
              <span className="avz-side-label">{l.label}</span>
            </NavLink>
          );
        })}
        {LOCKED_LINKS.map((l) => {
          const Icon = l.icon;
          return (
            <button
              key={l.label}
              type="button"
              className="avz-side-item avz-side-item--locked"
              data-testid={l.testid}
              aria-disabled="true"
              title="Próximamente"
              onClick={(e) => e.preventDefault()}
            >
              <Icon className="avz-side-icon" aria-hidden="true" />
              <span className="avz-side-label">{l.label}</span>
              <span className="avz-side-locked-pill">Pronto</span>
            </button>
          );
        })}
      </nav>

      <div className="avz-side-footer">
        {FOOTER_LINKS.map((l) => {
          const Icon = l.icon;
          return (
            <NavLink
              key={l.to}
              to={l.to}
              className="avz-side-item"
              data-testid={l.testid}
            >
              <Icon className="avz-side-icon" aria-hidden="true" />
              <span className="avz-side-label">{l.label}</span>
            </NavLink>
          );
        })}
        <LogoutButton variant="desktop" />
      </div>
    </aside>
  );
}

function MobileTabbar() {
  // F3 · Preview tabbar: Inicio | Entrenamiento | BUDDY | Práctica | Simulacro
  const items = [
    LINKS[0], LINKS[1], LINKS[2], LINKS[3], LINKS[4],
  ];
  return (
    <nav className="avz-tabbar" aria-label="Navegación inferior">
      {items.map((l) => {
        const Icon = l.icon;
        return (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.to === "/avanzo"}
            className="avz-tabbar-item"
            data-testid={`${l.testid}-mob`}
          >
            <Icon className="avz-tabbar-icon" aria-hidden="true" />
            <span>{l.label}</span>
          </NavLink>
        );
      })}
      <LogoutButton variant="mobile" />
    </nav>
  );
}

// Padres B2C: solo la vista Familia (sin navegación ni llamadas de estudiante).
function AvanzoParentShell() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="avz-parent-shell">
      <header className="flex items-center justify-between gap-3 px-4 sm:px-8 py-4 bg-white border-b border-slate-100">
        <span className="font-bold text-slate-900" data-testid="avz-parent-brand">Buddy AVANZA · Familia</span>
        <LogoutButton variant="mobile" />
      </header>
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6" data-testid="avz-parent-main">
        <Outlet />
      </main>
      <BuddyAvanzaLegalFooter dense />
    </div>
  );
}

function AvanzoShell() {
  const [showOnb, setShowOnb] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const onReopen = () => setShowOnb(true);
    window.addEventListener("avz:onboarding-reopen", onReopen);
    avanzoApi.paesOnboardingStatus()
      .then((r) => {
        if (cancelled) return;
        const d = r.data || {};
        if (d.completed) return;
        if (d.dismissed_at) {
          const dismissedMs = Date.parse(d.dismissed_at);
          if (!Number.isNaN(dismissedMs)) {
            const elapsed = Date.now() - dismissedMs;
            if (elapsed < 24 * 60 * 60 * 1000) return;
          }
        }
        setShowOnb(true);
      })
      .catch(() => { /* silent — do not block layout on network hiccup */ });
    return () => {
      cancelled = true;
      window.removeEventListener("avz:onboarding-reopen", onReopen);
    };
  }, []);

  return (
    <div className="avz-shell avz-shell--v2" data-testid="avz-shell">
      <NeuralBackground />
      <AvanzoOnboardingTour open={showOnb} onClose={() => setShowOnb(false)} />
      <S1Announcement blocked={showOnb} />

      <Sidebar />

      <main className="avz-main" data-testid="avz-main">
        <div className="avz-main-inner">
          <Outlet />
        </div>
      </main>

      <div className="avz-legal-anchor" data-testid="avz-legal-anchor">
        <BuddyAvanzaLegalFooter dense />
      </div>

      <MobileTabbar />
    </div>
  );
}

export default function AvanzoLayout(props) {
  return (
    <B2CGuard>
      <AvanzoRoot {...props} />
    </B2CGuard>
  );
}

function AvanzoRoot({ avanzoAccess }) {
  const { pathname } = useLocation();
  const role = avanzoAccess?.role;
  const brand = avanzoAccess?.brand;
  if (role === "teacher") {
    return <AvanzoTeacherPanel />;
  }
  if (role === "institutional_admin") {
    return <AvanzoStaffLanding role={role} brand={brand} />;
  }
  if (role === "parent") {
    if (!pathname.startsWith("/avanzo/familia")) return <Navigate to="/avanzo/familia" replace />;
    return <AvanzoParentShell />;
  }
  return <AvanzoShell />;
}
